import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { autoScaleVolumeMax, detectEquivalencePoint, isTitrationScenarioId } from "../engine/state/titrationCurve";
/**
 * Live pH-vs-titrant-volume overlay (session 342B).
 *
 * Renders a small canvas chart pinned to the top-right of the bench
 * HUD while a titration scenario is active. The X-axis is cumulative
 * mixture volume in mL (auto-scaled, 50 mL minimum); the Y-axis is
 * pH on a 0..14 scale. The component samples the inspection snapshot
 * at ~2 Hz and overlays the detected equivalence point as a vertical
 * dashed line labelled «نقطة التكافؤ».
 *
 * Visibility rules:
 *   - Active scenario id contains "titration" (per `isTitrationScenarioId`).
 *   - At least one valid sample has been collected.
 *   - The user has not dismissed the overlay via the close affordance.
 *
 * The buffer resets when the scenario id changes (new lesson) or the
 * mixture volume drops to ~0 (vessel emptied / cleared).
 *
 * Output is canvas-only — no DOM nodes per data point — so the chart
 * stays cheap on smartboards and Quest 3 even at 240+ samples.
 */
const CHART_W = 280;
const CHART_H = 180;
const PADDING_L = 36; // pH axis labels
const PADDING_R = 12;
const PADDING_T = 26;
const PADDING_B = 26; // mL axis labels + title
const SAMPLE_INTERVAL_MS = 500;
const MAX_SAMPLES = 240;
export function PhTitrationCurve({ inspection, scenarioId }) {
    const [samples, setSamples] = useState([]);
    const [dismissed, setDismissed] = useState(false);
    const lastSampleAt = useRef(0);
    const canvasRef = useRef(null);
    // Track the inspected vessel id so the buffer resets when the
    // student switches between vessels mid-lesson.
    const lastVesselId = useRef(null);
    const lastScenarioId = useRef(null);
    const titrationActive = isTitrationScenarioId(scenarioId);
    // Reset on scenario change.
    useEffect(() => {
        if (lastScenarioId.current !== scenarioId) {
            lastScenarioId.current = scenarioId;
            setSamples([]);
            setDismissed(false);
            lastSampleAt.current = 0;
        }
    }, [scenarioId]);
    // Reset on vessel change.
    useEffect(() => {
        const id = inspection?.id ?? null;
        if (lastVesselId.current !== id) {
            lastVesselId.current = id;
            setSamples([]);
            lastSampleAt.current = 0;
        }
    }, [inspection?.id]);
    // Sample on each inspection update (Stage emits ~5 Hz). Throttle
    // to one sample every SAMPLE_INTERVAL_MS to keep the buffer small.
    useEffect(() => {
        if (!titrationActive || !inspection)
            return;
        const now = performance.now();
        if (now - lastSampleAt.current < SAMPLE_INTERVAL_MS)
            return;
        const ph = inspection.physical.pH;
        const vol = inspection.physical.volumeMl;
        // Empty / cleared vessel — wipe history so a fresh refill starts
        // a new curve from the origin instead of rejoining the old one.
        if (vol < 0.5) {
            if (samples.length > 0)
                setSamples([]);
            lastSampleAt.current = now;
            return;
        }
        lastSampleAt.current = now;
        setSamples((prev) => {
            const next = prev.slice();
            next.push({ volumeMl: vol, pH: ph });
            while (next.length > MAX_SAMPLES)
                next.shift();
            return next;
        });
    }, [inspection, titrationActive, samples.length]);
    const equivalence = useMemo(() => detectEquivalencePoint(samples), [samples]);
    const volMax = useMemo(() => autoScaleVolumeMax(samples), [samples]);
    // Draw whenever samples or equivalence change.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = CHART_W * dpr;
        canvas.height = CHART_H * dpr;
        canvas.style.width = `${CHART_W}px`;
        canvas.style.height = `${CHART_H}px`;
        const ctx = canvas.getContext("2d");
        if (!ctx)
            return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        drawChart(ctx, samples, volMax, equivalence);
    }, [samples, volMax, equivalence]);
    if (!titrationActive || dismissed)
        return null;
    // Don't render until we have at least 2 samples — a single dot
    // is meaningless and the empty axes look broken on first paint.
    const validCount = samples.filter((s) => s.pH !== null).length;
    if (validCount < 2)
        return null;
    return (_jsxs("div", { className: "ph-curve-overlay", style: {
            position: "absolute",
            top: 76,
            right: 12,
            width: CHART_W,
            background: "rgba(12, 18, 28, 0.92)",
            border: "1px solid rgba(120, 180, 220, 0.35)",
            borderRadius: 8,
            padding: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
            zIndex: 30,
            pointerEvents: "auto",
            direction: "rtl"
        }, role: "figure", "aria-label": "\u0645\u0646\u062D\u0646\u0649 pH \u0645\u0642\u0627\u0628\u0644 \u0627\u0644\u062D\u062C\u0645", children: [_jsx("button", { type: "button", onClick: () => setDismissed(true), title: "\u0625\u062E\u0641\u0627\u0621", "aria-label": "\u0625\u062E\u0641\u0627\u0621 \u0645\u0646\u062D\u0646\u0649 pH", style: {
                    position: "absolute",
                    top: 2,
                    left: 2,
                    width: 28,
                    height: 28,
                    minWidth: 60,
                    minHeight: 60,
                    padding: 0,
                    margin: -16,
                    background: "transparent",
                    border: "none",
                    color: "#cfe1f5",
                    fontSize: 18,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    paddingTop: 16,
                    paddingLeft: 16
                }, children: "\u00D7" }), _jsx("canvas", { ref: canvasRef, style: { display: "block" } })] }));
}
function drawChart(ctx, samples, volMax, equivalence) {
    ctx.clearRect(0, 0, CHART_W, CHART_H);
    const x0 = PADDING_L;
    const y0 = PADDING_T;
    const w = CHART_W - PADDING_L - PADDING_R;
    const h = CHART_H - PADDING_T - PADDING_B;
    // Title
    ctx.fillStyle = "#cfe1f5";
    ctx.font = "12px system-ui, -apple-system, 'Segoe UI', sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "top";
    ctx.fillText("منحنى pH مقابل الحجم", CHART_W - PADDING_R, 6);
    // Grid + axes
    ctx.strokeStyle = "rgba(120, 180, 220, 0.18)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    // Y grid lines at pH 0,2,4,6,7,8,10,12,14
    const phMarks = [0, 2, 4, 6, 7, 8, 10, 12, 14];
    for (const p of phMarks) {
        const y = y0 + h - (p / 14) * h;
        ctx.moveTo(x0, y);
        ctx.lineTo(x0 + w, y);
    }
    // X grid every 5 mL
    const stepV = volMax <= 50 ? 10 : Math.ceil(volMax / 5 / 5) * 5;
    for (let v = 0; v <= volMax; v += stepV) {
        const x = x0 + (v / volMax) * w;
        ctx.moveTo(x, y0);
        ctx.lineTo(x, y0 + h);
    }
    ctx.stroke();
    // pH = 7 emphasis
    ctx.strokeStyle = "rgba(180, 220, 255, 0.45)";
    ctx.beginPath();
    const y7 = y0 + h - (7 / 14) * h;
    ctx.moveTo(x0, y7);
    ctx.lineTo(x0 + w, y7);
    ctx.stroke();
    // Axis labels
    ctx.fillStyle = "#9fb6cc";
    ctx.font = "10px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (const p of [0, 7, 14]) {
        const y = y0 + h - (p / 14) * h;
        ctx.fillText(String(p), x0 - 4, y);
    }
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (let v = 0; v <= volMax; v += stepV) {
        const x = x0 + (v / volMax) * w;
        ctx.fillText(String(v), x, y0 + h + 4);
    }
    ctx.textAlign = "right";
    ctx.fillText("الحجم (مل)", CHART_W - PADDING_R, y0 + h + 14);
    ctx.textAlign = "left";
    ctx.fillText("pH", 4, y0 - 2);
    // Curve — line + dots, light cyan on dark.
    ctx.strokeStyle = "#5eb6ff";
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    let started = false;
    for (const s of samples) {
        if (s.pH === null)
            continue;
        const x = x0 + Math.min(s.volumeMl / volMax, 1) * w;
        const y = y0 + h - (Math.max(0, Math.min(14, s.pH)) / 14) * h;
        if (!started) {
            ctx.moveTo(x, y);
            started = true;
        }
        else {
            ctx.lineTo(x, y);
        }
    }
    ctx.stroke();
    // Dots on top
    ctx.fillStyle = "#9ecfff";
    for (const s of samples) {
        if (s.pH === null)
            continue;
        const x = x0 + Math.min(s.volumeMl / volMax, 1) * w;
        const y = y0 + h - (Math.max(0, Math.min(14, s.pH)) / 14) * h;
        ctx.beginPath();
        ctx.arc(x, y, 1.6, 0, Math.PI * 2);
        ctx.fill();
    }
    // Equivalence point: vertical dashed line + label
    if (equivalence && equivalence.volumeMl >= 0 && equivalence.volumeMl <= volMax) {
        const ex = x0 + (equivalence.volumeMl / volMax) * w;
        ctx.save();
        ctx.strokeStyle = "#ffd25a";
        ctx.lineWidth = 1.4;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(ex, y0);
        ctx.lineTo(ex, y0 + h);
        ctx.stroke();
        ctx.restore();
        // Marker dot at the crossing
        const ey = y0 + h - (Math.max(0, Math.min(14, equivalence.pH)) / 14) * h;
        ctx.fillStyle = "#ffd25a";
        ctx.beginPath();
        ctx.arc(ex, ey, 3.5, 0, Math.PI * 2);
        ctx.fill();
        // Label — position above the line, clamped inside chart
        ctx.fillStyle = "#ffd25a";
        ctx.font = "11px system-ui, -apple-system, sans-serif";
        ctx.textBaseline = "top";
        const label = `نقطة التكافؤ · ${equivalence.volumeMl.toFixed(1)} مل`;
        const metrics = ctx.measureText(label);
        const labelW = metrics.width + 8;
        let lx = ex - labelW / 2;
        if (lx < x0)
            lx = x0;
        if (lx + labelW > x0 + w)
            lx = x0 + w - labelW;
        ctx.textAlign = "left";
        ctx.fillText(label, lx + 4, y0 + 2);
    }
}
